/**
 * 平台超级管理员：租户列表 / 创建 / 编辑 / 启停 / 五功能配置
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "../components/icons";
import {
  applyTenantFeatureToggle,
  collectFeatureDependencyWarnings,
} from "../tenant/featureDeps";
import { getTrpcErrorMessage, tenantApi } from "../tenant/api";
import {
  DEFAULT_TENANT_FEATURES,
  TENANT_FEATURE_KEYS,
  TENANT_FEATURE_LABELS,
  type TenantFeatures,
  type TenantRecord,
} from "../tenant/types";
import { isTenantActiveStatus, normalizeFeatures } from "../tenant/access";

const { Title, Text } = Typography;

function statusTag(status: string) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "active") return <Tag color="success">启用</Tag>;
  if (normalized === "disabled" || normalized === "suspended") {
    return <Tag color="error">停用</Tag>;
  }
  return <Tag>{status || "未知"}</Tag>;
}

export function TenantManagement() {
  const { message, modal } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [selected, setSelected] = useState<TenantRecord | null>(null);
  const [featureDraft, setFeatureDraft] = useState<TenantFeatures>(DEFAULT_TENANT_FEATURES);
  const [saving, setSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await tenantApi.list();
      setTenants(rows);
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "加载租户列表失败"));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  const dependencyWarnings = useMemo(
    () => collectFeatureDependencyWarnings(featureDraft),
    [featureDraft]
  );

  const openFeatures = (tenant: TenantRecord) => {
    setSelected(tenant);
    setFeatureDraft(normalizeFeatures(tenant.features));
    setFeaturesOpen(true);
  };

  const openEdit = (tenant: TenantRecord) => {
    setSelected(tenant);
    editForm.setFieldsValue({
      name: tenant.name,
    });
    setEditOpen(true);
  };

  const handleCreate = async (values: {
    name: string;
    code: string;
    features: TenantFeatures;
  }) => {
    setSaving(true);
    try {
      await tenantApi.create({
        name: values.name.trim(),
        code: values.code.trim(),
        features: normalizeFeatures(values.features),
      });
      message.success("租户已创建");
      setCreateOpen(false);
      createForm.resetFields();
      await loadTenants();
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "创建租户失败"));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (values: {
    name: string;
  }) => {
    if (!selected) return;
    setSaving(true);
    try {
      await tenantApi.update({
        tenantId: selected.id,
        name: values.name.trim(),
      });
      message.success("租户信息已更新");
      setEditOpen(false);
      setSelected(null);
      await loadTenants();
    } catch (err) {
      message.error(getTrpcErrorMessage(err, "更新租户失败"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = (tenant: TenantRecord) => {
    const active = isTenantActiveStatus(tenant.status);
    const nextStatus = active ? "disabled" : "active";
    modal.confirm({
      title: active ? "确认停用租户？" : "确认启用租户？",
      content: active
        ? `停用后，租户「${tenant.name}」下用户将无法进入业务系统。`
        : `启用后，租户「${tenant.name}」可恢复使用。`,
      okText: active ? "确认停用" : "确认启用",
      okButtonProps: { danger: active },
      cancelText: "取消",
      onOk: async () => {
        try {
          await tenantApi.updateStatus({ tenantId: tenant.id, status: nextStatus as "active" | "disabled" });
          message.success(active ? "租户已停用" : "租户已启用");
          await loadTenants();
        } catch (err) {
          message.error(getTrpcErrorMessage(err, "更新租户状态失败"));
          throw err;
        }
      },
    });
  };

  const handleSaveFeatures = () => {
    if (!selected) return;
    modal.confirm({
      title: "确认更新功能配置？",
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>
            将按当前配置更新租户「{selected.name}」的功能开关。
          </div>
          {dependencyWarnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="依赖提示"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {dependencyWarnings.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              }
            />
          )}
        </div>
      ),
      okText: "确认保存",
      cancelText: "取消",
      onOk: async () => {
        try {
          await tenantApi.updateFeatures({
            tenantId: selected.id,
            features: normalizeFeatures(featureDraft),
          });
          message.success("功能配置已更新");
          setFeaturesOpen(false);
          setSelected(null);
          await loadTenants();
        } catch (err) {
          message.error(getTrpcErrorMessage(err, "更新功能配置失败"));
          throw err;
        }
      },
    });
  };

  const columns: ColumnsType<TenantRecord> = [
    {
      title: "租户",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 180,
      render: (name, record) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.code || record.id}
          </Text>
        </Space>
      ),
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => statusTag(String(status || "")),
    },
    {
      title: "功能",
      key: "features",
      render: (_, record) => (
        <Space size={[4, 4]} wrap>
          {TENANT_FEATURE_KEYS.map((key) => (
            <Tag key={key} color={record.features[key] ? "processing" : "default"}>
              {TENANT_FEATURE_LABELS[key]}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString("zh-CN") : "-"),
    },
    {
      title: "操作",
      key: "actions",
      fixed: "right",
      width: 240,
      render: (_, record) => {
        const active = isTenantActiveStatus(record.status);
        return (
          <Space size="small" wrap>
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => openFeatures(record)}
            >
              功能
            </Button>
            <Button
              type="link"
              size="small"
              danger={active}
              onClick={() => handleToggleStatus(record)}
            >
              {active ? "停用" : "启用"}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="fade-in">
      <div className="page-title-row" style={{ marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            租户管理
          </Title>
          <Text type="secondary">仅平台超级管理员可管理全部租户与功能开关</Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => void loadTenants()} loading={loading}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.setFieldsValue({
                features: { ...DEFAULT_TENANT_FEATURES, dispatch_monitor: true },
              });
              setCreateOpen(true);
            }}
          >
            创建租户
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={tenants}
        scroll={{ x: 980 }}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 个租户`,
        }}
      />

      <Modal
        title="创建租户"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{
            features: { ...DEFAULT_TENANT_FEATURES, dispatch_monitor: true },
          }}
        >
          <Form.Item
            name="name"
            label="租户名称"
            rules={[{ required: true, message: "请输入租户名称" }]}
          >
            <Input placeholder="例如：华东运营中心" maxLength={64} />
          </Form.Item>
          <Form.Item
            name="code"
            label="租户编码"
            rules={[{ required: true, message: "请输入租户编码" }]}
          >
            <Input placeholder="唯一编码，如 east-ops" maxLength={64} />
          </Form.Item>
          <Form.Item label="初始功能" required>
            <Form.Item name="features" noStyle>
              <FeatureSwitchGroup />
            </Form.Item>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setCreateOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                创建
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑租户 - ${selected?.name || ""}`}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setSelected(null);
          editForm.resetFields();
        }}
        footer={null}
        destroyOnHidden
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item
            name="name"
            label="租户名称"
            rules={[{ required: true, message: "请输入租户名称" }]}
          >
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={() => setEditOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`功能配置 - ${selected?.name || ""}`}
        open={featuresOpen}
        onCancel={() => {
          setFeaturesOpen(false);
          setSelected(null);
        }}
        onOk={handleSaveFeatures}
        okText="保存"
        cancelText="取消"
        destroyOnHidden
        width={560}
      >
        <FeatureSwitchGroup value={featureDraft} onChange={setFeatureDraft} />
        {dependencyWarnings.length > 0 && (
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message="功能依赖不完整"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {dependencyWarnings.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            }
          />
        )}
      </Modal>
    </div>
  );
}

function FeatureSwitchGroup({
  value,
  onChange,
}: {
  value?: TenantFeatures;
  onChange?: (next: TenantFeatures) => void;
}) {
  const features = normalizeFeatures(value);
  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      {TENANT_FEATURE_KEYS.map((key) => (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 500 }}>{TENANT_FEATURE_LABELS[key]}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {key}
            </Text>
          </div>
          <Switch
            checked={features[key]}
            onChange={(checked) =>
              onChange?.(
                applyTenantFeatureToggle(features, key, checked)
              )
            }
          />
        </div>
      ))}
    </Space>
  );
}
